import React, { useEffect, useState, useRef } from "react";
import Papa from "papaparse";
import jsPDF from "jspdf";

const MAX_PER_LINE = 7; // max per horizontal row (top)
const MAX_PER_COL = 10; // max per vertical column (left/right)

const ContextDiagram = () => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [product, setProduct] = useState("Anthology Student SIS"); // NEW: selected center system
    const contentBoundsRef = useRef(null); // tight content bounds for PDF cropping

    const handleFileUpload = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setLoading(true);
        setError(null);
        Papa.parse(file, {
            header: true,
            skipEmptyLines: true,
            complete: (res) => {
                setRows(res.data || []);
                setLoading(false);
            },
            error: (err) => {
                setError(`Failed to parse CSV: ${err.message}`);
                setLoading(false);
            },
        });
    };

    const handleDownloadPDF = () => {
        const canvas = document.getElementById("contextCanvas");
        if (!canvas) return;

        // --- Crop to tight content bounds to remove side whitespace ---
        const bounds = contentBoundsRef.current;
        let srcX = 0,
            srcY = 0,
            srcW = canvas.width,
            srcH = canvas.height;
        const pad = 20; // small padding around content
        if (bounds) {
            srcX = Math.max(0, Math.floor(bounds.x - pad));
            srcY = Math.max(0, Math.floor(bounds.y - pad));
            srcW = Math.min(canvas.width - srcX, Math.ceil(bounds.w + pad * 2));
            srcH = Math.min(canvas.height - srcY, Math.ceil(bounds.h + pad * 2));
        }

        const temp = document.createElement("canvas");
        temp.width = srcW;
        temp.height = srcH;
        const tctx = temp.getContext("2d");
        tctx.drawImage(canvas, srcX, srcY, srcW, srcH, 0, 0, srcW, srcH);

        // Pick page orientation to best match the cropped diagram
        const ratio = temp.width / temp.height;
        const orientation = ratio >= 1 ? "l" : "p"; // landscape if wide

        const pdf = new jsPDF(orientation, "pt", "a4");
        const pageW = pdf.internal.pageSize.getWidth();
        const pageH = pdf.internal.pageSize.getHeight();

        const margin = 8; // tight page margins

        // --- Title (PDF only) ---
        const title = `${product} Context Diagram`; // UPDATED: use selected product
        const titleFontSize = 18;
        const titleGap = 12; // space below title
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(titleFontSize);
        const titleY = margin + titleFontSize;
        pdf.text(title, pageW / 2, titleY, { align: "center" });

        // Reserve space for title area when sizing the image
        const titleArea = titleFontSize + titleGap;

        const maxW = pageW - margin * 2;
        const maxH = pageH - margin * 2 - titleArea;

        // Scale to fit the page while preserving aspect ratio
        const scale = Math.min(maxW / temp.width, maxH / temp.height);
        const imgW = temp.width * scale;
        const imgH = temp.height * scale;

        // Center the diagram in the remaining area below the title
        const x = (pageW - imgW) / 2;
        const y = titleY + titleGap + (maxH - imgH) / 2;

        const imgData = temp.toDataURL("image/png", 1.0);
        pdf.addImage(imgData, "PNG", x, y, imgW, imgH, undefined, "FAST");

        pdf.save("context-diagram.pdf");
    };

    const asBoolX = (v) => (v || "").toString().trim().toLowerCase() === "x";
    const pick = (r, k) => (r?.[k] ?? "").toString().trim();

    const chunk = (arr, size) => {
        const out = [];
        for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
        return out;
    };

    const computeBuckets = (data) => {
        const topExport = [];
        const leftBi = [];
        const rightImport = [];

        for (const r of data) {
            const name = pick(r, "System Name");
            if (!name) continue;
            const ex = asBoolX(r["Export Only"]);
            const im = asBoolX(r["Import Only"]);
            const bi = asBoolX(r["Bi-Directional"]);
            const count = [ex, im, bi].filter(Boolean).length;
            if (count !== 1) continue; // ONLY one X allowed

            if (ex) topExport.push(name);
            else if (im) rightImport.push(name); // right side per latest spec
            else if (bi) leftBi.push(name);
        }
        return { topExport, leftBi, rightImport };
    };

    // --- Text wrapping helpers ---
    const splitWordToFit = (ctx, word, maxWidth) => {
        const parts = [];
        let cur = "";
        for (const ch of word) {
            const test = cur + ch;
            if (ctx.measureText(test).width <= maxWidth) {
                cur = test;
            } else {
                if (cur) parts.push(cur);
                cur = ch;
            }
        }
        if (cur) parts.push(cur);
        return parts;
    };

    const wrapLines = (ctx, text, maxWidth, font, padding = 10) => {
        const inner = Math.max(0, maxWidth - padding * 2);
        ctx.font = font;
        const words = text.split(/\s+/).filter(Boolean);
        const lines = [];
        let line = "";
        while (words.length) {
            const word = words.shift();
            const test = (line ? line + " " : "") + word;
            if (ctx.measureText(test).width <= inner) {
                line = test;
                continue;
            }
            if (line) {
                lines.push(line);
                line = "";
                words.unshift(word);
            } else {
                const parts = splitWordToFit(ctx, word, inner);
                if (parts.length) {
                    lines.push(parts[0]);
                    if (parts.length > 1) words.unshift(...parts.slice(1));
                }
            }
        }
        if (line) lines.push(line);
        return lines;
    };

    // --- Arrow helper ---
    const drawArrow = (ctx, x1, y1, x2, y2, { color = "#333", headStart = false, headEnd = true }) => {
        ctx.save();
        ctx.strokeStyle = color;
        ctx.fillStyle = color;
        ctx.lineWidth = 2;

        // shaft
        ctx.beginPath();
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
        ctx.stroke();

        const angle = Math.atan2(y2 - y1, x2 - x1);
        const head = 8;

        const drawHeadAt = (x, y, ang) => {
            ctx.beginPath();
            ctx.moveTo(x, y);
            ctx.lineTo(x - head * Math.cos(ang - Math.PI / 6), y - head * Math.sin(ang - Math.PI / 6));
            ctx.lineTo(x - head * Math.cos(ang + Math.PI / 6), y - head * Math.sin(ang + Math.PI / 6));
            ctx.closePath();
            ctx.fill();
        };

        if (headEnd) drawHeadAt(x2, y2, angle);
        if (headStart) drawHeadAt(x1, y1, angle + Math.PI);

        ctx.restore();
    };

    // --- Legend items depend on selected product ---
    const legendItemsFor = (centerName) => ([
        { label: `Export Only (${centerName} → External System)`, color: "#0b3c84" },
        { label: `Import Only (External System → ${centerName})`, color: "#cce5ff" },
        { label: "Bi-Directional (Both Directions)", color: "#ffa726" },
    ]);

    const measureLegend = (ctx, items) => {
        const pad = 10;
        const sw = 14; // swatch size
        const gap = 8;
        const titleLH = 18;
        const itemLH = 16;
        ctx.font = "bold 14px Arial";
        const titleW = ctx.measureText("Legend").width;
        ctx.font = "12px Arial";
        const textW = Math.max(...items.map((i) => ctx.measureText(i.label).width));
        const contentW = sw + gap + textW;
        const w = pad * 2 + Math.max(titleW, contentW);
        const h = pad * 2 + titleLH + gap + items.length * itemLH;
        return { w, h, pad, sw, gap, titleLH, itemLH };
    };

    const drawLegend = (ctx, x, y, metrics, items) => {
        const { w, h, pad, sw, gap, titleLH, itemLH } = metrics;
        // panel
        ctx.save();
        ctx.fillStyle = "rgba(255,255,255,0.9)";
        ctx.strokeStyle = "#333";
        ctx.lineWidth = 1;
        ctx.fillRect(x, y, w, h);
        ctx.strokeRect(x, y, w, h);

        // title
        ctx.fillStyle = "#000";
        ctx.font = "bold 14px Arial";
        ctx.textAlign = "left";
        ctx.textBaseline = "top";
        ctx.fillText("Legend", x + pad, y + pad);

        // items
        ctx.font = "12px Arial";
        let curY = y + pad + titleLH + gap;
        items.forEach((it) => {
            // swatch
            ctx.fillStyle = it.color;
            ctx.fillRect(x + pad, curY + 1, sw, sw);
            ctx.strokeStyle = "#333";
            ctx.strokeRect(x + pad, curY + 1, sw, sw);
            // label
            ctx.fillStyle = "#000";
            ctx.fillText(it.label, x + pad + sw + gap, curY);
            curY += itemLH;
        });
        ctx.restore();

        return { x, y, w, h };
    };

    const draw = (data) => {
        const canvas = document.getElementById("contextCanvas");
        if (!canvas) return;
        const ctx = canvas.getContext("2d");

        const { topExport, leftBi, rightImport } = computeBuckets(data);

        // layout constants
        const padding = 40;
        const boxW = 200;
        const boxH = 44;
        const gap = 16;

        const centerW = 260;
        const centerH = 60;

        // Split groups
        const topRows = chunk(topExport, MAX_PER_LINE); // above center, horizontal rows
        const leftCols = chunk(leftBi, MAX_PER_COL); // left of center, vertical cols
        const rightCols = chunk(rightImport, MAX_PER_COL); // right of center, vertical cols

        // Measure width/height needs
        const topMaxRowW = topRows.length ? Math.max(...topRows.map((r) => r.length * (boxW + gap) - gap)) : 0;
        const leftTotalW = leftCols.length * (boxW + gap);
        const rightTotalW = rightCols.length * (boxW + gap);

        const horizNeed = leftTotalW + Math.max(centerW, topMaxRowW) + rightTotalW + 200; // padding around columns

        const topTotalH = topRows.length ? topRows.length * (boxH + gap) - gap : 0;
        const sideMaxH = Math.max(
            0,
            ...leftCols.map((c) => c.length * (boxH + gap) - gap),
            ...rightCols.map((c) => c.length * (boxH + gap) - gap)
        );
        const vertNeed = topTotalH + 60 + centerH + 60 + Math.max(200, sideMaxH) + 200;

        const width = Math.max(1100, padding * 2 + horizNeed);
        const height = Math.max(700, padding * 2 + vertNeed);

        canvas.width = width;
        canvas.height = height;

        // clear
        ctx.clearRect(0, 0, width, height);

        // ===== No title on canvas =====
        // We start the top row directly after the padding.
        const firstTopY = padding + 40; // top row position (closest to top border)
        const topToCenterGap = 60; // generous space from top row -> center system

        // Horizontal center balances left/right column widths
        const cx = width / 2 + (leftTotalW - rightTotalW) / 2;

        // center box FIRST
        const centerY = firstTopY + boxH + topToCenterGap;
        const centerX = cx - centerW / 2;
        ctx.fillStyle = "#f0f0f0";
        ctx.strokeStyle = "#333";
        ctx.lineWidth = 1;
        ctx.fillRect(centerX, centerY - centerH / 2, centerW, centerH);
        ctx.strokeRect(centerX, centerY - centerH / 2, centerW, centerH);
        ctx.fillStyle = "#000";
        ctx.font = "bold 16px Arial";
        const centerLabel = product; // UPDATED: use selected product
        const clw = ctx.measureText(centerLabel).width;
        ctx.fillText(centerLabel, centerX + (centerW - clw) / 2, centerY + 6);

        // Compute placements of all side boxes (but DON'T draw yet)
        const topRects = [];
        const leftRects = [];
        const rightRects = [];

        // --- TOP rows (stack upward from firstTopY) ---
        topRows.forEach((row, idx) => {
            const totalW = row.length * (boxW + gap) - gap;
            const startX = cx - totalW / 2;
            const y = firstTopY - idx * (boxH + gap); // stack upward
            row.forEach((name, j) => {
                const x = startX + j * (boxW + gap);
                topRects.push({ x, y, w: boxW, h: boxH, name, color: "#0b3c84" });
            });
        });

        // Safe vertical band for side columns
        const topBlockBottom = topRows.length ? firstTopY + boxH : padding + 20;
        const safeTop = topBlockBottom + gap;
        const safeBottom = height - padding - 20;
        const safeBandHeight = Math.max(0, safeBottom - safeTop);

        // --- LEFT columns ---
        const firstLeftX = centerX - 60 - boxW;
        leftCols.forEach((col, idx) => {
            const totalH = col.length * (boxH + gap) - gap;
            const x = firstLeftX - idx * (boxW + gap);
            let startY = safeTop + (safeBandHeight - totalH) / 2;
            if (startY < safeTop) startY = safeTop;
            if (startY + totalH > safeBottom) startY = Math.max(safeTop, safeBottom - totalH);
            col.forEach((name, j) => {
                const y = startY + j * (boxH + gap);
                leftRects.push({ x, y, w: boxW, h: boxH, name, color: "#ffa726" });
            });
        });

        // --- RIGHT columns ---
        const firstRightX = centerX + centerW + 60;
        rightCols.forEach((col, idx) => {
            const totalH = col.length * (boxH + gap) - gap;
            const x = firstRightX + idx * (boxW + gap);
            let startY = safeTop + (safeBandHeight - totalH) / 2;
            if (startY < safeTop) startY = safeTop;
            if (startY + totalH > safeBottom) startY = Math.max(safeTop, safeBottom - totalH);
            col.forEach((name, j) => {
                const y = startY + j * (boxH + gap);
                rightRects.push({ x, y, w: boxW, h: boxH, name, color: "#cce5ff" });
            });
        });

        // --- LINES/ARROWS (draw BEFORE boxes so boxes appear on top) ---
        // Export Only (dark blue): center -> top box; head at system
        topRects.forEach((r) => {
            const startX = centerX + centerW / 2;
            const startY = centerY - centerH / 2;
            const endX = r.x + r.w / 2;
            const endY = r.y + r.h; // bottom center of system box
            drawArrow(ctx, startX, startY, endX, endY, {
                color: "#0b3c84",
                headStart: false,
                headEnd: true,
            });
        });

        // Bi-Directional (orange): center <-> left box; heads at both ends
        leftRects.forEach((r) => {
            const startX = centerX; // left edge center of center box
            const startY = centerY;
            const endX = r.x + r.w; // right edge center of system box
            const endY = r.y + r.h / 2;
            drawArrow(ctx, startX, startY, endX, endY, {
                color: "#ffa726",
                headStart: true,
                headEnd: true,
            });
        });

        // Import Only (light blue): right system -> center; head at center
        rightRects.forEach((r) => {
            const startX = r.x; // left edge center of system box
            const startY = r.y + r.h / 2;
            const endX = centerX + centerW; // right edge center of center box
            const endY = centerY;
            drawArrow(ctx, startX, startY, endX, endY, {
                color: "#cce5ff",
                headStart: false,
                headEnd: true,
            });
        });

        // --- Compute tight content bounds for PDF cropping (include legend bounds) ---
        let minX = centerX,
            minY = centerY - centerH / 2,
            maxX = centerX + centerW,
            maxY = centerY + centerH / 2;
        const expand = (r) => {
            minX = Math.min(minX, r.x);
            minY = Math.min(minY, r.y);
            maxX = Math.max(maxX, r.x + r.w);
            maxY = Math.max(maxY, r.y + r.h);
        };
        topRects.forEach(expand);
        leftRects.forEach(expand);
        rightRects.forEach(expand);

        // Measure legend and place it centered below content with a small gap
        const legendItems = legendItemsFor(product); // UPDATED: product-aware labels
        const legendMetrics = measureLegend(ctx, legendItems);
        const legendGap = 16;
        const legendY = maxY + legendGap;
        const contentCenterX = (minX + maxX) / 2;
        const legendX = contentCenterX - legendMetrics.w / 2; // centered under diagram

        // Expand bounds to include legend
        minX = Math.min(minX, legendX);
        minY = Math.min(minY, legendY);
        maxX = Math.max(maxX, legendX + legendMetrics.w);
        maxY = Math.max(maxY, legendY + legendMetrics.h);

        contentBoundsRef.current = { x: minX, y: minY, w: maxX - minX, h: maxY - minY };

        // --- BOXES (draw AFTER lines so they appear above the lines) ---
        const drawWrappedBox = (rect) => {
            const { x, y, w, h, color, name } = rect;
            // box
            ctx.fillStyle = color;
            ctx.fillRect(x, y, w, h);
            ctx.strokeStyle = "#333";
            ctx.strokeRect(x, y, w, h);
            // text
            const font = "bold 13px Arial";
            const lineHeight = 14;
            const paddingInner = 10;
            const lines = wrapLines(ctx, name, w, font, paddingInner);
            const totalTextH = lines.length * lineHeight;
            const textCX = x + w / 2;
            let textY = y + (h - totalTextH) / 2 + lineHeight / 2;
            const prevAlign = ctx.textAlign;
            const prevBaseline = ctx.textBaseline;
            ctx.textAlign = "center";
            ctx.textBaseline = "middle";
            ctx.fillStyle = "#fff";
            ctx.font = font;
            lines.forEach((ln) => {
                ctx.fillText(ln, textCX, textY);
                textY += lineHeight;
            });
            ctx.textAlign = prevAlign;
            ctx.textBaseline = prevBaseline;
        };

        topRects.forEach(drawWrappedBox);
        leftRects.forEach(drawWrappedBox);
        rightRects.forEach(drawWrappedBox);

        // --- Draw Legend last so it's on top ---
        drawLegend(ctx, legendX, legendY, legendMetrics, legendItems);
    };

    useEffect(() => {
        if (!rows?.length) return;
        draw(rows);
        // Re-render when product changes too (so legend/center update live)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rows, product]);

    return (
        <div className="bb-container" style={{ maxHeight: "90vh", overflowY: "auto" }}>
            <h1 className="bb-title">Context Diagram</h1>

            {/* NEW: Product selector */}
            <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8 }}>
                <label htmlFor="productSel" style={{ fontWeight: 600 }}>Center System:</label>
                <select
                    id="productSel"
                    className="bb-input"
                    value={product}
                    onChange={(e) => setProduct(e.target.value)}
                >
                    <option>Anthology Blackboard LMS</option>
                    <option>Anthology Student SIS</option>
                    <option>Anthology Reach CRM</option>
                </select>
            </div>

            <input type="file" accept=".csv" onChange={handleFileUpload} className="bb-input" />

            <div style={{ marginTop: 10, marginBottom: 10 }}>
                <button onClick={handleDownloadPDF} className="bb-button" style={{ padding: "6px 12px" }}>
                    Download PDF
                </button>
            </div>

            {loading && <p>Loading…</p>}
            {!loading && rows.length === 0 && <p>Upload a CSV to render the diagram.</p>}
            {error && <div className="bb-error" style={{ marginTop: 8 }}>{error}</div>}
            <div style={{ overflowX: "auto" }}>
                <canvas id="contextCanvas" style={{ border: "1px solid #ccc", marginTop: 14, display: "block" }} />
            </div>
        </div>
    );
};

export default ContextDiagram;
